import { useState, useRef, useCallback, useEffect } from 'react';
import { SunProgressOptionsProps } from '../models/sun-progress-options.model';

/**
 * Manages the “scroll to fill sun” logic (wheel + touch),
 * shows text when progress ≥ 1, then fires onUnlock exactly once.
 */
export function useSunProgress({ hasAgreed, isSafari, onUnlock }: SunProgressOptionsProps) {
    const [progress, setProgress] = useState(0);
    const [textShown, setTextShown] = useState(false);
    const unlockedRef = useRef(false);
    const unlockCalledRef = useRef(false);

    const triggerUnlock = useCallback(() => {
        if (!unlockCalledRef.current) {
            unlockCalledRef.current = true;
            onUnlock();
        }
    }, [onUnlock]);

    // Wheel / desktop scroll
    useEffect(() => {
        if (!hasAgreed) return;

        if (isSafari) {
            // instantly complete on Safari
            setProgress(1);
            setTextShown(true);
            setTimeout(() => {
                unlockedRef.current = true;
                setTimeout(triggerUnlock, 300);
            }, 800);
            return;
        }

        const handleWheel = (e: WheelEvent) => {
            if (unlockedRef.current) return;
            e.preventDefault();
            setProgress((prev) => {
                const next = Math.min(1, Math.max(prev + e.deltaY * 0.004, 0));
                if (next >= 1 && !unlockedRef.current) {
                    setTextShown(true);
                    setTimeout(() => {
                        unlockedRef.current = true;
                        setTimeout(triggerUnlock, 300);
                    }, 800);
                }
                return next;
            });
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, [hasAgreed, isSafari, triggerUnlock]);

    // Touch / mobile scroll
    useEffect(() => {
        let touchStartY: number | null = null;

        const handleTouchStart = (e: TouchEvent) => {
            touchStartY = e.touches[0].clientY;
        };
        const handleTouchMove = (e: TouchEvent) => {
            if (!hasAgreed || unlockedRef.current || touchStartY === null) return;
            e.preventDefault();
            const touchCurrentY = e.touches[0].clientY;
            const deltaY = touchStartY - touchCurrentY;

            setProgress((prev) => {
                const next = Math.min(1, Math.max(prev + deltaY * 0.006, 0));
                if (next >= 1 && !unlockedRef.current) {
                    setTextShown(true);
                    setTimeout(() => {
                        unlockedRef.current = true;
                        setTimeout(triggerUnlock, 300);
                    }, 800);
                }
                return next;
            });
            touchStartY = touchCurrentY;
        };
        const handleTouchEnd = () => {
            touchStartY = null;
        };

        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });
        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [hasAgreed, triggerUnlock]);

    return { progress, textShown };
}
